import { Component, EventEmitter, Output } from '@angular/core';
import { read, utils, writeFile } from 'xlsx';
import { NgxSpinnerService } from "ngx-spinner";
import { AppToastService } from 'src/app/shared/toast-service/toast-service';
import { UploadService } from '../services/upload.service';
import { ExcelImportOptions } from './excel-import-options.model';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { System } from 'src/app/shared/models/thermal-methods/System.model';
import { ThermalMethod } from 'src/app/shared/models/thermal-methods/ThermalMethod.model';
import { SystemThermalMethodMapping } from 'src/app/shared/models/thermal-methods/SystemThermalMethodMapping.model';
import { FacadeInsertUnit } from 'src/app/shared/models/thermal-methods/FacadeInsertUnit.model';
import { FacadeSpacer } from 'src/app/shared/models/thermal-methods/FacadeSpacer.model';
import { FrenchUfValuesWindow } from 'src/app/shared/models/thermal-methods/FrenchUfValuesWindow.model';
import { GermanUfEquationFacade } from 'src/app/shared/models/thermal-methods/GermanUfEquationFacade.model';
import { GermanUfEquationWindow } from 'src/app/shared/models/thermal-methods/GermanUfEquationWindow.model';
import { GermanUfValuesSlidingDoor } from 'src/app/shared/models/thermal-methods/GermanUfValuesSlidingDoor.model';
import { GermanUfValuesWindow } from 'src/app/shared/models/thermal-methods/GermanUfValuesWindow.model';

@Component({
    selector: 'app-excel-variety-imports',
    templateUrl: './excel-variety-imports.component.html',
    styleUrls: ['./excel-variety-imports.component.scss'],
    standalone: false
})
export class ExcelVarietyImportsComponent {

  rowsFromExcel: any[] | any;
  displayError: boolean = false;
  errorMessage: string = "";
  selectedThermalOption: string | null = null;
  thermalOptions = ExcelImportOptions;
  thermalExcelUploadForm!: FormGroup;
  columnHeadersFromExcel!: string[];

  @Output()
  close: EventEmitter<void> = new EventEmitter<void>();

  constructor(
    private spinner: NgxSpinnerService,
    public toastService: AppToastService,
    private fb: FormBuilder,
    private uploadService: UploadService
    ) {}

  ngOnInit() {
    this.thermalExcelUploadForm = this.fb.group({
      thermalOption: new FormControl(),
      excelFile: new FormControl({value: null, disabled: !this.selectedThermalOption})
    });

    this.thermalExcelUploadForm.get("thermalOption")?.valueChanges.subscribe(f => {
      this.handleOptionSelect(f);
    })
  }

  /**
   * Handles the thermal option select, used to enable the mapping and upload of the excel file.
   * @param $event the thermal option select event.
   */
  handleOptionSelect($event: any) {
    this.selectedThermalOption = this.thermalExcelUploadForm.controls['thermalOption'].value;

    if (!!this.selectedThermalOption) {
      this.thermalExcelUploadForm.controls['excelFile'].enable();
    }
  }

  /**
   * Used in the template to get and display the data from the excel sheet.
   * @param obj the row to get the values from.
   * @returns the values of the object for display in the template.
   */
  getObjectValues(obj: {}) {
    return Object.values(obj);
  }

  /**
   * Handles the file select event from the template.
   * @param $event the file select so we can read its data.
   */
  handleExcelFileSelect($event: any) {
    this.displayError = false;
    const files = $event.target.files;
    if (files.length) {
      const file = files[0];
      const reader = new FileReader();
      reader.onload = (event: any) => {
        const wb = read(event.target.result);
        const sheets = wb.SheetNames;

        if (sheets.length) {
          const rows: {}[] = utils.sheet_to_json(wb.Sheets[sheets[0]], {defval: null});

          if (!Array.isArray(rows) || rows.length === 0 || typeof rows[0] !== "object") {
            throw new Error("Can't determine Excel sheet headers.");
          } else {
            this.columnHeadersFromExcel = Object.keys(rows[0]);
          }
          this.rowsFromExcel = this.mapRowsByMethod(this.selectedThermalOption as string, rows);
        };
      };
      reader.readAsArrayBuffer(file);
    }
  }

  /**
   * Maps the rows from the excel sheet to the appropriate model.
   * @param selectedThermalOption the selected thermal option from the dropdown.
   * @param rows the rows from the excel sheet.
   * @returns the rows mapped to the appropriate model.
   * @throws an error if the rows can't be mapped to any thermal model.
   */
  mapRowsByMethod(selectedThermalOption: string, rows: {}[]) {
    let processedRows: {}[] | null = null;

    switch (selectedThermalOption) {
      case 'System':
        processedRows = rows.map((row: {}) => new System(row));
        break;
      case 'ThermalMethod':
        processedRows =  rows.map((row: {}) => new ThermalMethod(row));
        break;
      case 'SystemThermalMethodMapping':
        processedRows =  rows.map((row: {}) => new SystemThermalMethodMapping(row));
        break;
      case 'FacadeInsertUnit':
        processedRows =  rows.map((row: {}) => new FacadeInsertUnit(row));
        break;
      case 'FacadeSpacer':
        processedRows =  rows.map((row: {}) => new FacadeSpacer(row));
        break;
      case 'FrenchUfValuesWindow':
        processedRows =  rows.map((row: {}) => new FrenchUfValuesWindow(row));
        break;
      case 'GermanUfEquationFacade':
        processedRows =  rows.map((row: {}) => new GermanUfEquationFacade(row));
        break;
      case 'GermanUfEquationWindow':
        processedRows =  rows.map((row: {}) => new GermanUfEquationWindow(row));
        break;
      case 'GermanUfValuesSlidingDoor':
        processedRows =  rows.map((row: {}) => new GermanUfValuesSlidingDoor(row));
        break;
      case 'GermanUfValuesWindow':
        processedRows =  rows.map((row: {}) => new GermanUfValuesWindow(row));
        break;
    }
    
    return processedRows;
  }

  /**
   * Uploads the Excel data.
   * @throws an error if the upload is unsuccessful.
   */
  uploadExcelObject() {
    if (!!!this.rowsFromExcel) {
      this.showError("Please upload an Excel file first.");
    } else {
      this.spinner.show();
      if (this.selectedThermalOption && this.rowsFromExcel) {
        this.uploadService.uploadThermalData(this.selectedThermalOption, this.rowsFromExcel).subscribe({
          error: () => {
            this.spinner.hide();
            this.showError("Upload unsuccessful. Please try again.");
          },
          complete: () => {
            this.spinner.hide();
            this.closeModal();
            this.toastService.show('Data uploaded successfully', { classname: 'bg-success text-light', delay: 4000 });
          }

        })
      } else {
        this.showError("Please select a thermal option and upload a valid excel file for that data type.");
        this.spinner.hide();
      }
    }
  }

  /**
   * Shows an error message in the modal above the submit button.
   * @param message the error message to display
   */
  showError(message: string) {
    this.displayError = true;
    this.errorMessage = message;
  }
  
  closeModal() {
    this.close.emit();
  }
}
